import type { RoofHandle } from "building-types/handles/docEntityHandleTypes";
import type { GableRoofExtrusion, GableRoofVerticalRails } from "building-types/roofs/gableRoofExtrusionTypes";
import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import { DEFAULT_ROOFING_MATERIAL_EXTENSION } from "building-types/roofs/roofConstants";
import type { RoofEdge } from "building-types/roofs/roofEdgeTypes";
import type { RoofFace } from "building-types/roofs/roofFace";
import { RoofFaceType } from "building-types/roofs/roofFace";
import type { RoofPerimeterRail } from "building-types/roofs/roofPerimeterTypes";
import type { RoofBlocks } from "building-types/roofs/roofSystemTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import { RoofTruncationType, TruncationSlopeType, type RoofSlopeOptions } from "building-types/settings/roofBaseTypes";
import type { RoofElevationPlanSettings, RoofPlanSettings } from "building-types/settings/roofPlanSettings";
import type { RoofSettings } from "building-types/settings/roofSettings";
import type { EvaluatedRoofEdgeOverridesTable } from "building-types/settings/roofTypes";
import {
  RoofSide,
  type InputParamToResolveEdgeSideOptions,
  type RidgeRail,
  type RoofPerimeters,
  type RoofTruncationOptions,
} from "building-types/settings/roofTypes";
import { RIDGE_VENT } from "common/constants/buildingObjects";
import { getMaxDepthErrorHandle } from "doc/building/buildingGenerationIssuesTools";
import { MULTIGABLE_BSP_EPSILON, ridgeRailNew, roofSlopeOptionsNew } from "doc/utils/roofTools";
import type { Face } from "geom/brep";
import { faceNew } from "geom/brep";
import { addConvexPlanesToBSPLeaf } from "geom/bsp";
import type { ClipRailContext } from "geom/bspClipping";
import { clippingNodeNew, clipRail3 } from "geom/bspClipping";
import { ANGLE_EPSILON, DIST_EPSILON } from "geom/core";
import { line2Collinear } from "geom/line2";
import { line3Tangent } from "geom/line3";
import {
  planeByThreePoints,
  planeIsNotNull,
  planeMoveAlongNormal,
  planeNew,
  planeNormal,
  planeReverse,
} from "geom/plane";
import { planeBSPFromPlane } from "geom/planeBSP";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2EdgeCount,
  polyLine2EdgeEnd,
  polyLine2EdgeLength,
  polyLine2EdgeNormal,
  polyLine2EdgeStart,
  polyLine2EdgeTangent,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import {
  polyLine3ByTwoPoints,
  polyLine3EdgeCount,
  polyLine3EdgeEnd,
  polyLine3EdgeStart,
  polyLine3ExtendStart,
} from "geom/polyLine3";
import { vec3AngleBetween, vec3Cross, vec3SequenceIsTurning } from "geom/vec3";
import { updateRoofBSP } from "../multiGableRoofClipContext";
import { roofFaceNew } from "../roofFaceNew";
import { clipRoofFaces } from "./roofClipping";
import { isRoofEdgeValidForCustomHipEdge, isRoofSideEdge } from "./roofEdges";
import { buildRidgeCapProfile } from "./roofRidges";
import { isRoofEdgeAlignedToSide } from "./roofSlopes";
import { DEFAULT_ROOF_TRUNCATION_OPTIONS } from "./roofTruncationDefaults";

export function hasSubRoofTruncatedEnd(roof: GeneratedRoof, isEnd: boolean): boolean {
  if (!roof) {
    return false;
  }

  const { roofVolume } = roof;
  const { verticalRails, baseHeight, peakHeight } = roofVolume;
  const { truncatedStartHeight, truncatedEndHeight } = verticalRails;
  const truncatedHeight = isEnd ? truncatedEndHeight : truncatedStartHeight;
  const baseToPeak = peakHeight - baseHeight;

  return truncatedHeight > DIST_EPSILON && truncatedHeight < baseToPeak - DIST_EPSILON;
}

interface AddTruncatedRidgesOptions {
  peakRail: PolyLine2;
  verticalRails: GableRoofVerticalRails;
  extrusion: GableRoofExtrusion;
  ridgeRails: RidgeRail[];
  baseHeight: number;
  endSide: boolean;
  roofSettings: RoofSettings;
}

export function addTruncatedRidgeRails({
  peakRail,
  verticalRails,
  extrusion,
  ridgeRails,
  baseHeight,
  endSide,
  roofSettings,
}: AddTruncatedRidgesOptions) {
  const { roofingMaterialExtension = DEFAULT_ROOFING_MATERIAL_EXTENSION } = roofSettings;
  const { leftFaces, rightFaces, startHipFaces, endHipFaces } = extrusion;
  const { startLeftRail, startRightRail, endLeftRail, endRightRail, truncatedStartHeight, truncatedEndHeight } =
    verticalRails;

  const leftRail = endSide ? endRightRail : startLeftRail;
  const rightRail = endSide ? endLeftRail : startRightRail;
  const leftSideFaces = endSide ? rightFaces : leftFaces;
  const rightSideFaces = endSide ? leftFaces : rightFaces;
  const hipFaces = endSide ? endHipFaces : startHipFaces;
  const truncatedHeight = endSide ? truncatedEndHeight : truncatedStartHeight;

  if (!(leftRail.length >= 6)) {
    console.assert(false);
  }
  if (!(rightRail.length >= 6)) {
    console.assert(false);
  }

  const addSideRidge = (verticalRail: PolyLine3, isRightSide: boolean) => {
    const sideFaces = isRightSide ? rightSideFaces : leftSideFaces;
    const railEdgeCount = verticalRail ? polyLine3EdgeCount(verticalRail) : 0;
    const l = Math.min(sideFaces.length, hipFaces.length, railEdgeCount);

    let rail: PolyLine3 | undefined = undefined;
    let leftSlope = 0;
    let rightSlope = 0;

    let clippingPlanes: Plane[] | undefined = undefined;

    for (let i = 0; i < l; i++) {
      const sideFaceIndex = sideFaces.length - 1 - i;
      const hipFaceIndex = hipFaces.length - 1 - i;
      const railEdgeIndex = railEdgeCount - 1 - i;

      const [x0, y0, z0] = polyLine3EdgeStart(verticalRail, railEdgeIndex);
      const [x1, y1, z1] = polyLine3EdgeEnd(verticalRail, railEdgeIndex);

      const [ridgeTangentX, ridgeTangentY, ridgeTangentZ] = line3Tangent(x0, y0, z0, x1, y1, z1);
      const [ridgePlaneUX, ridgePlaneUY, ridgePlaneUZ] = vec3Cross(
        0,
        0,
        1,
        ridgeTangentX,
        ridgeTangentY,
        ridgeTangentZ,
      );
      const [ridgePlaneVX, ridgePlaneVY, ridgePlaneVZ] = vec3Cross(
        ridgeTangentX,
        ridgeTangentY,
        ridgeTangentZ,
        ridgePlaneUX,
        ridgePlaneUY,
        ridgePlaneUZ,
      );

      const [leftNormalX, leftNormalY, leftNormalZ] = isRightSide
        ? planeNormal(hipFaces[hipFaceIndex].plane)
        : planeNormal(sideFaces[sideFaceIndex].plane);
      const [rightNormalX, rightNormalY, rightNormalZ] = isRightSide
        ? planeNormal(sideFaces[sideFaceIndex].plane)
        : planeNormal(hipFaces[hipFaceIndex].plane);

      const leftAngle = vec3AngleBetween(
        leftNormalX,
        leftNormalY,
        leftNormalZ,
        ridgePlaneVX,
        ridgePlaneVY,
        ridgePlaneVZ,
      );
      const rightAngle = vec3AngleBetween(
        rightNormalX,
        rightNormalY,
        rightNormalZ,
        ridgePlaneVX,
        ridgePlaneVY,
        ridgePlaneVZ,
      );

      const leftSegmentSlope = Math.tan(leftAngle);
      const rightSegmentSlope = Math.tan(rightAngle);

      let railSegment = polyLine3ByTwoPoints(x0, y0, z0, x1, y1, z1);
      const segmentClippingPlanes: Plane[] = [];
      if (i === 0) {
        const [peakUX, peakUY] = polyLine2EdgeTangent(peakRail, 0, endSide);
        const [peakX, peakY] = endSide ? polyLine2EdgeEnd(peakRail, 0) : polyLine2EdgeStart(peakRail, 0);

        const peakPlane = planeNew(peakX, peakY, 0, peakUX, peakUY, 0, 0, 0, 1);
        if (isRightSide) {
          planeReverse(peakPlane);
        }
        segmentClippingPlanes.push(peakPlane);
      }
      if (i === l - 1) {
        const [peakUX, peakUY] = polyLine2EdgeTangent(peakRail, 0, !endSide);
        const [endUX, endUY, endUZ] = vec3Cross(0, 0, 1, peakUX, peakUY, 0);
        const endPlane = planeNew(x0, y0, z0, endUX, endUY, endUZ, 0, 0, 1);
        planeMoveAlongNormal(endPlane, roofingMaterialExtension);

        const sidePlane = planeNew(
          x0,
          y0,
          z0,
          isRightSide ? -peakUX : peakUX,
          isRightSide ? -peakUY : peakUY,
          0,
          0,
          0,
          1,
        );
        planeMoveAlongNormal(sidePlane, roofingMaterialExtension);

        l > 1 ? segmentClippingPlanes.push(sidePlane) : 0;
        segmentClippingPlanes.push(endPlane);
        railSegment = polyLine3ExtendStart(railSegment, roofingMaterialExtension);
      }

      if (!rail || !clippingPlanes) {
        rail = railSegment;
        leftSlope = leftSegmentSlope;
        rightSlope = rightSegmentSlope;
        clippingPlanes = segmentClippingPlanes;
      } else if (
        Math.abs(leftSlope - leftSegmentSlope) < ANGLE_EPSILON &&
        Math.abs(rightSlope - rightSegmentSlope) < ANGLE_EPSILON &&
        !vec3SequenceIsTurning(x0, y0, z0, rail[0], rail[1], rail[2], rail[3], rail[4], rail[5])
      ) {
        rail[0] = x0;
        rail[1] = y0;
        rail[2] = z0;
        clippingPlanes.push(...segmentClippingPlanes);
      } else {
        ridgeRails.push(
          ridgeRailNew(
            rail,
            RIDGE_VENT,
            buildRidgeCapProfile(leftSlope, rightSlope, roofSettings),
            leftSlope,
            rightSlope,
            baseHeight + truncatedHeight,
            false,
            false,
            clippingPlanes,
          ),
        );
        rail = railSegment;
        leftSlope = leftSegmentSlope;
        rightSlope = rightSegmentSlope;
        clippingPlanes = segmentClippingPlanes;
      }
    }
    if (rail && rail.length > 0) {
      ridgeRails.push(
        ridgeRailNew(
          rail,
          RIDGE_VENT,
          buildRidgeCapProfile(leftSlope, rightSlope, roofSettings),
          leftSlope,
          rightSlope,
          baseHeight + truncatedHeight,
          false,
          false,
          clippingPlanes,
        ),
      );
    }
  };

  addSideRidge(leftRail, false);
  addSideRidge(rightRail, true);
}

export function getResolvedStartTruncationOptions(options: InputParamToResolveEdgeSideOptions): RoofTruncationOptions {
  return getResolvedTruncationOptions(options, false);
}

export function getResolvedEndTruncationOptions(options: InputParamToResolveEdgeSideOptions): RoofTruncationOptions {
  return getResolvedTruncationOptions(options, true);
}

function getResolvedTruncationOptions(
  {
    boundingPerimeter,
    exactPerimeter,
    startEdge,
    roofSettings,
    heelHeightOffsets,
    roofSideOverrides = {},
    roofEdgeOverrides = {},
    legacyRoofSlope = false,
    legacyHeelHeightOffsets = false,
  }: InputParamToResolveEdgeSideOptions,
  isEnd: boolean,
): RoofTruncationOptions {
  const getSlopeOptionsFromSingleSlope = (slope: number | undefined | null): RoofSlopeOptions[] | undefined => {
    if (slope === undefined || slope === null) {
      return undefined;
    }

    return [roofSlopeOptionsNew(slope)];
  };

  const edgeOverride = getSubRoofEdgeOverrideTruncation({
    boundingPerimeter,
    exactPerimeter,
    startEdge,
    side: isEnd ? RoofSide.END : RoofSide.START,
    roofEdgeOverrides,
  });

  const sideOverride = roofSideOverrides[isEnd ? RoofSide.END : RoofSide.START];
  const slopeOverride = sideOverride?.slope;
  const slopeOverrideOptions = sideOverride?.slopeOptions;
  const truncationTypeOverride = sideOverride?.truncationType;

  let resolvedType =
    edgeOverride?.truncationType ??
    sideOverride?.truncationType ??
    (isEnd ? roofSettings.endTruncationType : roofSettings.startTruncationType);

  const resolvedSlopeType =
    edgeOverride?.truncationSlopeType ??
    sideOverride?.truncationSlopeType ??
    (isEnd ? roofSettings.endTruncationSlopeType : roofSettings.startTruncationSlopeType);

  const slopeOptionsFromSettings = getSlopeOptionsFromSingleSlope(
    isEnd ? roofSettings.endTruncationSlope : roofSettings.startTruncationSlope,
  );

  let resolvedSlopeOptions =
    getSlopeOptionsFromSingleSlope(edgeOverride?.truncationSlope) ??
    getSlopeOptionsFromSingleSlope(sideOverride?.truncationSlope) ??
    slopeOptionsFromSettings;

  let resolvedHeightFactor =
    edgeOverride?.heelHeightOffset ??
    sideOverride?.heelHeightOffset ??
    (isEnd ? roofSettings.endTruncationHeightFactor : roofSettings.startTruncationHeightFactor);

  const resolvedNormalizedHeight =
    edgeOverride?.normalizedHeelHeightOffset ??
    sideOverride?.normalizedHeelHeightOffset ??
    (isEnd ? roofSettings.endTruncationNormalizedHeight : roofSettings.startTruncationNormalizedHeight);

  const normalizedHeight = resolvedNormalizedHeight ?? DEFAULT_ROOF_TRUNCATION_OPTIONS.normalizedHeight;

  // if the heel height offset is set, it overrides the truncation parameters
  if (resolvedHeightFactor !== undefined && resolvedHeightFactor !== null) {
    if (!legacyHeelHeightOffsets && !normalizedHeight) {
      resolvedHeightFactor -= heelHeightOffsets.refOffset;
    }

    resolvedType =
      truncationTypeOverride ??
      (resolvedHeightFactor < DIST_EPSILON ? RoofTruncationType.HIP : RoofTruncationType.TRUNCATED);
    resolvedHeightFactor = Math.max(resolvedHeightFactor, 0);
  }

  // if the slope is set and the slope type is set to override, it overrides the truncation parameters
  if (resolvedSlopeType === TruncationSlopeType.OVERRIDE) {
    if (slopeOverrideOptions && slopeOverrideOptions.length > 0) {
      resolvedSlopeOptions = slopeOverrideOptions;
    } else if (slopeOverride !== undefined && slopeOverride !== null) {
      resolvedSlopeOptions = [roofSlopeOptionsNew(slopeOverride)];
    } else if (!legacyRoofSlope && slopeOptionsFromSettings && slopeOptionsFromSettings.length > 0) {
      resolvedSlopeOptions = slopeOptionsFromSettings;
    } else if (!legacyRoofSlope && roofSettings.slopes && roofSettings.slopes.length > 0) {
      resolvedSlopeOptions = roofSettings.slopes;
    } else if (!legacyRoofSlope && roofSettings.slope !== undefined && roofSettings.slope !== null) {
      resolvedSlopeOptions = [roofSlopeOptionsNew(roofSettings.slope)];
    }
  }

  const type = resolvedType ?? DEFAULT_ROOF_TRUNCATION_OPTIONS.type;
  const heightFactor = resolvedHeightFactor ?? DEFAULT_ROOF_TRUNCATION_OPTIONS.heightFactor;
  const slopeOptions = resolvedSlopeOptions ?? DEFAULT_ROOF_TRUNCATION_OPTIONS.slopeOptions;
  const slopeType = resolvedSlopeType ?? DEFAULT_ROOF_TRUNCATION_OPTIONS.slopeType;

  return {
    type,
    heightFactor,
    normalizedHeight,
    slopeType,
    slopeOptions,
  };
}

interface GetSubRoofEdgeOverrideTruncationOptions {
  readonly boundingPerimeter: PolyLine2;
  readonly exactPerimeter: PolyLine2;
  readonly startEdge: number;
  readonly side: RoofSide;
  readonly roofEdgeOverrides?: EvaluatedRoofEdgeOverridesTable;
}

interface GetSubRoofEdgeOverrideTruncationResult {
  readonly truncationType?: RoofTruncationType;
  readonly heelHeightOffset?: number;
  readonly normalizedHeelHeightOffset?: boolean;
  readonly truncationSlopeType?: TruncationSlopeType;
  readonly truncationSlope?: number;
}

function getSubRoofEdgeOverrideTruncation({
  boundingPerimeter,
  exactPerimeter,
  startEdge,
  side,
  roofEdgeOverrides = {},
}: GetSubRoofEdgeOverrideTruncationOptions): GetSubRoofEdgeOverrideTruncationResult {
  let truncationOptions: GetSubRoofEdgeOverrideTruncationResult = {};

  for (let i = 0, l = polyLine2EdgeCount(exactPerimeter); i < l; i++) {
    const { truncationType, heelHeightOffset, normalizedHeelHeightOffset, truncationSlopeType, truncationSlope } =
      roofEdgeOverrides[i] ?? {};

    if (
      !isRoofEdgeAlignedToSide({
        boundingPerimeter,
        exactPerimeter,
        startEdge,
        side,
        edgeIndex: i,
      })
    ) {
      continue;
    }

    truncationOptions = {
      ...truncationOptions,
      truncationType,
      heelHeightOffset,
      normalizedHeelHeightOffset,
      truncationSlopeType,
      truncationSlope,
    };
  }

  return truncationOptions;
}

interface GetExtraHipFacesOptions {
  readonly subRoofId: string;
  readonly roofEdgeOverrides: EvaluatedRoofEdgeOverridesTable;
  readonly height: number;
  readonly perimeter: PolyLine2;
  readonly blocks: RoofBlocks;
  readonly perimeters: RoofPerimeters;
  readonly baseHeight: number;
  readonly roofPerimeterRails: RoofPerimeterRail[];
  readonly exactEdges: RoofEdge[];
  readonly extrusion: GableRoofExtrusion;
  readonly hasStartExtension: boolean;
  readonly hasEndExtension: boolean;
  readonly startFrontFace: Face | null;
  readonly endFrontFace: Face | null;
  readonly shedRoofHalfFace?: Face | null;
  readonly annotationSettings: RoofPlanSettings & RoofElevationPlanSettings;
  readonly issues: BuildingGenerationIssue[];
}

export function getExtraHipFaces({
  subRoofId,
  roofEdgeOverrides,
  height,
  perimeter,
  blocks,
  perimeters,
  baseHeight,
  roofPerimeterRails,
  exactEdges,
  extrusion,
  hasStartExtension,
  hasEndExtension,
  startFrontFace,
  endFrontFace,
  shedRoofHalfFace,
  annotationSettings,
  issues,
}: GetExtraHipFacesOptions): RoofFace[] {
  const extraHipFaces: RoofFace[] = [];

  if (Object.keys(roofEdgeOverrides).length === 0) {
    return extraHipFaces;
  }

  const roofHandle: RoofHandle = {
    type: "ROOF",
    id: subRoofId,
  };

  const maxDepthErrorHandle = getMaxDepthErrorHandle(issues, roofHandle);

  const clipExtraRoofTopBSP = updateRoofBSP({
    rootBSP: null,
    subRoofId: "",
    hasStartExtension,
    hasEndExtension,
    perimeter,
    extrusion,
    startFrontFace,
    endFrontFace,
    exactEdges,
    height,
    shedRoofHalfFace: shedRoofHalfFace ?? null,
    maxDepthErrorHandle,
  });

  for (const edgeId in roofEdgeOverrides) {
    const { slope = Number.POSITIVE_INFINITY } = roofEdgeOverrides[edgeId];

    if (slope === Number.POSITIVE_INFINITY) {
      continue;
    }

    const edgeIndex = parseInt(edgeId);

    if (
      isRoofSideEdge({ blocks, perimeters }, edgeIndex) ||
      !isRoofEdgeValidForCustomHipEdge({ exactEdges }, edgeIndex)
    ) {
      continue;
    }

    const { edge } = exactEdges[edgeIndex];

    const [x0, y0] = polyLine2EdgeStart(perimeter, edgeIndex);
    const [x1, y1] = polyLine2EdgeEnd(perimeter, edgeIndex);
    const [nX, nY] = polyLine2EdgeNormal(perimeter, edgeIndex, true);
    const length = polyLine2EdgeLength(perimeter, edgeIndex);

    const hipPlane = planeByThreePoints(x0, y0, baseHeight, x1, y1, baseHeight, x0 + nX, y0 + nY, baseHeight + slope);

    // check that no roof perimeter rail is above the hip plane
    if (!planeIsNotNull(hipPlane) || !isValidHipEdgePlane(roofPerimeterRails, hipPlane, edge)) {
      issues.push({
        type: BuildingGenerationIssueType.ROOF_EDGE_SLOPE_ISSUE,
        handle: {
          type: "EDGE",
          parent: roofHandle,
          shapeId: "outline",
          edgeId: edgeIndex,
        },
      });
      continue;
    }

    const loop = polyLine2New([length, 0, 0, 0, 0, 1000, length, 1000, length, 0]);

    const extraRoofFace = roofFaceNew(
      RoofFaceType.HIP,
      RoofSide.INVALID,
      faceNew([loop], hipPlane),
      false,
      annotationSettings,
    );

    extraHipFaces.push(
      ...clipRoofFaces([extraRoofFace], {
        clippingNodes: [clippingNodeNew(clipExtraRoofTopBSP, true)],
        epsilon: MULTIGABLE_BSP_EPSILON,
      }),
    );
  }

  return extraHipFaces;
}

function isValidHipEdgePlane(
  roofPerimeterRails: RoofPerimeterRail[],
  hipPlane: Plane | null,
  edge: PolyLine2,
): boolean {
  if (!hipPlane) {
    return false;
  }

  const planeBSP = planeBSPFromPlane(hipPlane);
  const bsp = addConvexPlanesToBSPLeaf([planeBSP]);

  // check that no roof perimeter rail is fully above the hip plane
  const options: ClipRailContext = {
    clippingNodes: [clippingNodeNew(bsp, true)],
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  for (const { rail, baseEdge } of roofPerimeterRails) {
    // ignore the edge that is being checked
    if (line2Collinear(baseEdge[0], baseEdge[1], baseEdge[2], baseEdge[3], edge[0], edge[1], edge[2], edge[3])) {
      continue;
    }

    const belowRails = clipRail3({
      rail,
      options,
    });

    if (belowRails.length === 0) {
      return false;
    }
  }

  return true;
}
